import { getQueryKey } from '@trpc/react-query'
import { trpc } from 'api'
import { queryClient, trpcProxyClient } from 'api/ApiProvider'
import { Resource, i18n } from 'i18next'
import isEqual from 'lodash/isEqual'
import AppConfig from './AppConfig'
import getLogger from 'utils/getLogger'
import createMmkvStorage, { PersistedData } from 'utils/mmkvStorage'
import useRemoteConfigParams from 'storage/useRemoteConfigParams'

type Translation = string | { [key: string]: Translation }
type Translations = Record<string, Translation>

type PersistedTranslations = PersistedData<Resource>

const logger = getLogger('i18nBackend', 'warn')

const getKey = (language: string, namespace: string) =>
  `${language}:${namespace}`

const { persist, load, remove, clear } =
  createMmkvStorage<Translations>('translations')

export const loadMulti = (languages: string[], namespaces: string[]) => {
  let updatedAt = 0
  const loaded = Object.fromEntries(
    languages.map((language) => {
      const namespacesObj = Object.fromEntries(
        namespaces.map((namespace) => {
          const data = load(getKey(language, namespace))
          updatedAt = !updatedAt
            ? data.updatedAt
            : Math.min(updatedAt, data.updatedAt)
          return [namespace, data.data ?? undefined]
        })
      )
      return [language, namespacesObj]
    })
  )

  return {
    updatedAt,
    data: loaded,
  } as PersistedTranslations
}

const persistMulti = (
  languages: string[],
  namespaces: string[],
  data: Record<string, Record<string, Translations>>,
  add?: (language: string, namespace: string, data: Translations) => void
) => {
  languages.forEach((language) => {
    namespaces.forEach((namespace) => {
      const value: Translations | undefined = data?.[language]?.[namespace]
      if (!value) {
        return
      }
      persist(getKey(language, namespace), value)
      add?.(language, namespace, value)
    })
  })
}

const createI18nBackend = (i18n: i18n) =>
  ({
    type: 'backend',
    //   init: function (services, backendOptions, i18nextOptions) {
    //     /* use services and options */
    //   },
    init: () => {
      logger.log('Init')
    },
    // or new since i18next v22.1.0
    read: async (language: string, namespace: string) => {
      // Clear persisted data if needed
      // clear()

      // Load persisted data
      const initial = load(getKey(language, namespace)) // TEMP
      const configState = useRemoteConfigParams.getState()

      try {
        const input = {
          locale: language,
          namespace,
          config: configState.params,
        }

        const query = queryClient.fetchQuery({
          queryKey: getQueryKey(trpc.locales.loadNamespace, input),
          queryFn: async () => {
            const data = await trpcProxyClient.locales.loadNamespace.query(
              input
            )
            if (!data) {
              logger.warn('No data for namespace', namespace)
              return initial.data || {}
            }

            persist(getKey(language, namespace), data)
            i18n.addResourceBundle(language, namespace, data, false, true)
            if (initial.data && !isEqual(initial.data, data)) {
              // Force a re-render with new values if
              i18n.changeLanguage(i18n.language)
            }
            return data
          },
          // Prevent for fetching too many times
          initialData: initial.data,
          initialDataUpdatedAt: initial.updatedAt,
          staleTime: AppConfig.i18n.staleTime,
        })

        if (initial.data) {
          logger.log('Returning initial data')
          return initial.data
        }
        // If we don't have initial data return the query promise
        // We wait here so we can catch any potential errors
        const data = await query
        return data || {}
      } catch (ex) {
        logger.warn(ex)
        return initial.data || {}
      }
    },

    readMulti: async (languages: string[], namespaces: string[]) => {
      const initial = loadMulti(languages, namespaces)
      const configState = useRemoteConfigParams.getState()

      const input = {
        locales: languages,
        namespaces,
        config: configState.params,
      }

      try {
        const query = queryClient.fetchQuery({
          queryKey: getQueryKey(trpc.locales.loadMulti, input),
          queryFn: async () => {
            const data = await trpcProxyClient.locales.loadMulti.query(input)

            if (!data) {
              logger.warn('No data for multi load', input)
              return initial.data || {}
            }

            persistMulti(
              languages,
              namespaces,
              data,
              (language, namespace, res) => {
                i18n.addResourceBundle(language, namespace, res, false, true)
              }
            )

            if (initial.data && !isEqual(initial.data, data)) {
              // Force a re-render with new values if
              i18n.changeLanguage(i18n.language)
            }
            return data
          },
          // Prevent for fetching too many times
          initialData: initial.data,
          initialDataUpdatedAt: initial.updatedAt,
          staleTime: AppConfig.i18n.staleTime,
        })

        if (initial.data) {
          logger.log('Returning initial data')
          return initial.data
        }
        // If we don't have initial data return the query promise
        // We wait here so we can catch any potential errors
        const data = await query
        return data || {}
      } catch (ex) {
        console.error('Error fetching translations', languages, namespaces)
        logger.warn(ex)
        return initial.data || {}
      }
    },

    // only used in backends acting as cache layer
    // save: function(language, namespace, data) {
    //   // store the translations
    // },

    // create: function(languages, namespace, key, fallbackValue) {
    //   /* save the missing translation */
    // }
  } as const)

export default createI18nBackend
